import React, { useEffect, useMemo, useRef } from 'react';
import { Chip } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { StatementHistoryItem } from '@store/generated/billing';
import { format } from 'date-fns/format';

import { useStatementsHistory } from '../../../store';
import { BillingTable } from '../BillingTable';
import type { ColumnDefinition } from '../types';

interface StatementsHistoryProps {
  page: number;
  perPage: number;
  sort?: string;
  order: 'asc' | 'desc';
  filters: {
    from?: string;
    to?: string;
  };
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
}

type HistoryRow = StatementHistoryItem & Record<string, unknown> & { id: string };

const formatCents = (cents: number | undefined | null): string => {
  if (cents == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  } catch {
    return dateString;
  }
};

const TRIGGER_TYPE_LABELS: Record<string, string> = {
  ERA_RECEIPT: 'ERA Receipt',
  MANUAL: 'Manual',
};

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
};

const columns: ColumnDefinition<HistoryRow>[] = [
  {
    field: 'trigger_type',
    header: 'Trigger',
    sortable: false,
    priority: 'high',
    sticky: true,
    width: 130,
    format: (value) => (
      <Chip
        label={TRIGGER_TYPE_LABELS[value as string] ?? (value as string)}
        size="small"
        color="default"
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    field: 'status',
    header: 'Status',
    sortable: false,
    priority: 'high',
    width: 120,
    format: (value) => (
      <Chip
        label={value as string}
        size="small"
        color={STATUS_COLORS[value as string] ?? 'default'}
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    field: 'total_invoices_created',
    header: 'Invoices',
    sortable: true,
    align: 'center',
    priority: 'medium',
    width: 90,
  },
  {
    field: 'total_amount_cents',
    header: 'Amount',
    sortable: true,
    align: 'right',
    priority: 'high',
    width: 120,
    format: (value) => formatCents(value as number),
  },
  {
    field: 'triggered_by',
    header: 'Triggered By',
    sortable: false,
    priority: 'low',
    mobileHidden: true,
    width: 140,
    format: (value) => (value as string) || 'System',
  },
  {
    field: 'completed_at',
    header: 'Completed',
    sortable: true,
    priority: 'medium',
    width: 170,
    format: (value) => formatDate(value as string),
  },
  {
    field: 'error_count',
    header: 'Errors',
    sortable: true,
    align: 'center',
    priority: 'medium',
    width: 80,
    format: (value) => {
      const count = value as number;
      if (!count) return '—';
      return (
        <Chip label={String(count)} size="small" color="error" variant="outlined" />
      );
    },
  },
];

export const StatementsHistory: React.FC<StatementsHistoryProps> = ({
  page,
  perPage,
  sort,
  order,
  filters,
  onPageChange,
  onPerPageChange,
  onSort,
}) => {
  const { payment } = useEventTracking();
  const { items, totalCount, isLoading } = useStatementsHistory({
    page,
    perPage,
    dateFrom: filters.from,
    dateTo: filters.to,
  });
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTrackedView.current) {
      hasTrackedView.current = true;
      payment.statementsQueue.historyViewed({ total_count: totalCount });
    }
  }, [isLoading, totalCount, payment]);

  const rows = useMemo(
    () => items.map((item) => ({ ...item, id: item.statement_run_id })),
    [items],
  );

  return (
    <BillingTable
      data={rows}
      columns={columns}
      totalItems={totalCount}
      page={page}
      perPage={perPage}
      sortField={sort}
      sortOrder={order}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
      onSort={onSort}
      loading={isLoading}
      selectable={false}
      emptyMessage="No statement history yet"
    />
  );
};
